/**
 * UI-тексты страницы аэропорта (AirportPageContent).
 * Только специфика страницы; общие «Все статьи»/«Популярные вопросы» — в servicePage.
 * Интерполируемые строки (надзаголовки с названием аэропорта, метки расстояния,
 * трансфер из IATA) — функции. Склонения/формат чисел остаются в компоненте.
 */
export const airportPageUi = {
    en: {
        // Not-found fallback
        notFound: 'Airport page not found',
        goHome: 'Go home',
        // Breadcrumbs / nav
        home: 'Home',
        // Anchors (subnav)
        anchor: {
            overview: 'Overview',
            map: 'Map',
            transport: 'How to get there',
            routes: 'Routes',
            essentials: 'Essentials',
            nearby: 'Where to go',
            articles: 'Articles',
            faq: 'Q&A',
        },
        // 01 · Overview
        overviewEyebrow: (name) => `${name}. Overview`,
        overviewTitle: 'About the airport',
        factIata: 'IATA code',
        factCountry: 'Country',
        factCoords: 'Coordinates',
        factNearestCity: 'Nearest city',
        // 02 · Map
        mapEyebrow: (name) => `${name}. Map`,
        mapTitle: 'Location',
        mapAria: (name) => `Map of ${name}`,
        mapNote: '// schematic · airport & nearby cities',
        legendAirport: 'Airport',
        legendNearby: 'Nearby cities',
        // 03 · How to get there
        transportEyebrow: (name) => `${name}. How to get there`,
        transportTitle: 'Getting from the airport',
        transportLead: 'Transfers, taxis, public transport and car rentals — choose the most convenient way to your hotel.',
        // 04 · Routes
        routesEyebrow: (name) => `${name}. Routes`,
        routesTitle: 'Popular routes',
        routeLearnMore: 'Learn more',
        // 05 · Essentials
        essentialsEyebrow: (name) => `${name}. Essentials`,
        essentialsTitle: 'What you need on the way',
        essentialsLead: 'Connectivity, protection and peace of mind from the first minute after landing — set it up in advance.',
        essentialsTag: 'connectivity',
        essentialsConnect: 'Connect',
        // 06 · Where to go
        nearbyEyebrow: (name) => `${name}. Where to go`,
        nearbyTitle: 'Nearby cities',
        allCities: 'All cities',
        transferFrom: (iata) => `Transfer from ${iata}`,
        // 07 · Articles
        articlesEyebrow: (name) => `${name}. Helpful resources`,
        articlesTitle: 'Articles',
        // 08 · FAQ
        faqEyebrow: (name) => `${name}. Q&A`,
        faqLead: 'Quick answers about transfers, routes and preparing for arrival or departure.',
        // Units
        km: 'km',
    },
    ru: {
        notFound: 'Страница аэропорта не найдена',
        goHome: 'На главную',
        home: 'Главная',
        anchor: {
            overview: 'Обзор',
            map: 'На карте',
            transport: 'Как добраться',
            routes: 'Направления',
            essentials: 'Полезное',
            nearby: 'Куда поехать',
            flights: 'Авиабилеты',
            articles: 'Статьи',
            faq: 'Вопросы',
        },
        overviewEyebrow: (name) => `${name}. Обзор`,
        overviewTitle: 'Кратко об аэропорте',
        factIata: 'Код IATA',
        factCountry: 'Страна',
        factCoords: 'Координаты',
        factNearestCity: 'Ближайший город',
        mapEyebrow: (name) => `${name}. На карте`,
        mapTitle: 'Где находится',
        mapAria: (name) => `Схема расположения аэропорта ${name}`,
        mapNote: '// схема · аэропорт и города поблизости',
        legendAirport: 'Аэропорт',
        legendNearby: 'Города поблизости',
        transportEyebrow: (name) => `${name}. Как добраться`,
        transportTitle: 'Как добраться из аэропорта',
        transportLead: 'Трансферы, такси, общественный транспорт и аренда авто — выберите удобный способ доехать до отеля.',
        routesEyebrow: (name) => `${name}. Направления`,
        routesTitle: 'Популярные направления',
        routeLearnMore: 'Подробнее',
        essentialsEyebrow: (name) => `${name}. Полезное`,
        essentialsTitle: 'Что понадобится в дороге',
        essentialsLead: 'Связь, защита и подстраховка с первой минуты после прилёта — подключите заранее.',
        essentialsTag: 'связь',
        essentialsConnect: 'Подключить',
        nearbyEyebrow: (name) => `${name}. Куда поехать`,
        nearbyTitle: 'Города рядом',
        allCities: 'Все города',
        transferFrom: (iata) => `Трансфер из ${iata}`,
        flightsEyebrow: (name) => `${name}. Авиабилеты`,
        flightsTitle: 'Авиабилеты',
        flightsLead: 'Цены на ближайшие даты, прямые рейсы и пересадки — на Aviasales. Город вылета можно поменять на странице.',
        flightsNoFlights: 'Регулярных рейсов во Влёру пока нет — летят в Тирану, оттуда до Влёры около двух часов на машине.',
        flightsGo: 'Смотреть цены',
        vloTransferTitle: 'Трансфер из аэропорта Тираны во Влёру',
        vloTransferGo: 'Заказать трансфер',
        flightCity: {
            MOW: 'Москва', LED: 'Санкт-Петербург', IST: 'Стамбул', BEG: 'Белград', MSQ: 'Минск', ALA: 'Алматы',
            TIV: 'Тиват', TGD: 'Подгорица', TIA: 'Тирана', TBS: 'Тбилиси', BUS: 'Батуми', KUT: 'Кутаиси',
            DOH: 'Доха', ZRH: 'Цюрих', GVA: 'Женева', LJU: 'Любляна',
        },
        articlesEyebrow: (name) => `${name}. Полезные материалы`,
        articlesTitle: 'Статьи',
        faqEyebrow: (name) => `${name}. Вопросы и ответы`,
        faqLead: 'Коротко о трансфере, маршрутах и подготовке к прилёту или вылету.',
        km: 'км',
    },
};
